import { createElement, CSSProperties } from "react";
import { camelCase, DynamicFormControl, DynamicFormDefinition, FormSubmission } from "../../../";
import CheckboxControl from "./checkbox-control";
import DateControl from "./date-control";
import FileUploadControl from "./file-upload-control";
import HtmlControl from "./html-control";
import MaskedTextControl from "./masked-text-control";
import PhoneControl from "./phone-control";
import RadioButtonControl from "./radio-button-control";
import SelectControl from "./select-control";
import SignatureControl from "./signature-control";
import TextControl from "./text-control";
import CancelButtonControl from "./cancel-button-control";
import SubmitButtonControl from "./submit-button-control";
import { Grid } from "@mui/material";

/** Generic wrapper component for a form control */
function FormControlContainer({ control, form, hideSubmit, formLoading, loading, readOnly, formSubmission, onCancel, onStateUpdate }:
    {
        control: DynamicFormControl,
        form: DynamicFormDefinition | null,
        hideSubmit: boolean,
        formLoading: boolean,
        loading: boolean,
        readOnly?: boolean,
        formSubmission?: FormSubmission,
        onCancel?: (() => void) | undefined,
        onStateUpdate: ((state: string | string[] | null, id: string | undefined) => void),
    }) {

    const controls = new Map([['text', TextControl],
    ['date', DateControl],
    ['phone', PhoneControl],
    ['select', SelectControl],
    ['masked', MaskedTextControl],
    ['checkbox', CheckboxControl],
    ['upload', FileUploadControl],
    ['html', HtmlControl],
    ['signature', SignatureControl],
    ['radio', RadioButtonControl]]);

    function getControl(formControl: DynamicFormControl) {
        if (formControl == null || typeof formControl !== 'object') {
            return;
        }

        formControl.setState = state => {
            formControl.state = state;
            //setCanDisplay(canDisplayItem(formControl));
            onStateUpdate(state, formControl.id);
        }

        if (formControl.fileUploadOptions != null) {
            formControl.fileUploadOptions.setFiles = files => {
                if (formControl.fileUploadOptions) {
                    formControl.fileUploadOptions.files = files;
                }
            }
        }

        formControl.setDisabled = disabled => {
            formControl.disabled = disabled;
        }

        formControl.updater = {};

        if (readOnly) {
            formControl.disabled = true;
        }

        if (formSubmission != null && formSubmission.formSubmissionData != null) {
            formControl.value = getValueFromSubmissionData(formControl, formSubmission.formSubmissionData);

            // console.log(formControl.label + ": " + formControl.value);
        }

        const control = controls.get(formControl.type);
        if (control != null) {
            return createElement(control, formControl);
        }

        // Handle special form flow controls
        if (!readOnly) {
            switch (formControl.type) {
                case "cancel":
                    return (
                        <CancelButtonControl disabled={formLoading || loading} onCancel={cancel} label={formControl.label} />
                    );
                case "submit":
                    return (
                        <SubmitButtonControl disabled={formLoading || loading} label={formControl.label}></SubmitButtonControl>
                    );
            }
        }
    }

    function getValueFromSubmissionData(control: DynamicFormControl, submission: any): string | null {
        let value: string | null = null;

        for (var key in submission) {
            const camelCaseLabel = control.jsonName != null || control.label == null ? null : camelCase(control.label);
            if (control.jsonName === key || camelCaseLabel === key) {
                switch (control.type) {
                    case "checkbox":
                        value = getCheckboxValue(control, submission);
                        break;
                    default:
                        value = submission[key];
                        break;
                }
                value = submission[key];
            }
        }

        return value;
    }

    function getCheckboxValue(control: DynamicFormControl, submission: any): string | null {
        let value: string | null = null;

        const selectedPropertyOptions = control.selectPropertyOptions?.filter(option => Object.keys(submission).some(key => key === option.jsonName && submission[key]));

        return value;
    }

    function cancel(): void {
        if (onCancel) {
            onCancel();
        }
    }

    return control.canDisplay ? (
        <Grid key={control.id + '-grid'} item style={control.style as CSSProperties}
            xs={control.gridSize?.xs}
            sm={control.gridSize?.sm}
            md={control.gridSize?.md}
            lg={control.gridSize?.lg}
            xl={control.gridSize?.xl}>
            {getControl(control)}
        </Grid>
    ) : null;
}

export default FormControlContainer;